import React, { useEffect, useState, useCallback } from 'react';
import {useParams, useNavigate} from 'react-router-dom';import axios from 'axios';
import {MessageDisplay} from '@/components/MessageDisplay';import {Button} from '@/components/ui/button';import {Helmet} from 'react-helmet-async';import {BACKEND_URL, API_URL as API} from '@/utils/apiConfig';import {BookmarkPlus, BookmarkCheck, MessageSquareText, Tag, Check, ChevronDown} from 'lucide-react';import {toast} from 'sonner';import {useTheme} from '@/contexts/ThemeContext';

import { useTranslation } from 'react-i18next';

import { homeTarget } from '@/components/ShareHomeButton';
const FOLLOW_UP_PATTERNS = [
  /^would you like/i,
  /^do you want/i,
  /^shall (i|we)/i,
  /^should (i|we)/i,
  /^is there (anything|something|a specific)/i,
  /^are there (any|other)/i,
  /^did (that|this) (make sense|help|answer)/i,
  /^does (that|this) (make sense|help|answer)/i,
  /^what (would|do|else|other|aspect)/i,
  /^how (would|do|can|else)/i,
  /^let me know/i,
  /^feel free/i,
  /^i'd love to (explore|help|dive|discuss)/i,
  /^i can (also|help|explore|provide)/i,
];

function stripFollowUpQuestions(text) {
  const paragraphs = text.split('\n\n');
  const filtered = paragraphs.filter((p) => {
    const trimmed = p.trim();
    if (!trimmed.endsWith('?') && !FOLLOW_UP_PATTERNS.some((re) => re.test(trimmed))) return true;
    if (trimmed.length < 200 && (trimmed.endsWith('?') || FOLLOW_UP_PATTERNS.some((re) => re.test(trimmed)))) {
      return false;
    }
    return true;
  });
  return filtered.join('\n\n').trimEnd();
}

function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const THEMES = {
  classic: { bg: '#FFFFFF', text: '#1a1a2e', accent: '#6366f1', secondary: '#64748b', border: '#e2e8f0', surface: '#f8fafc' },
  dark: { bg: '#0f172a', text: '#f1f5f9', accent: '#818cf8', secondary: '#94a3b8', border: '#1e293b', surface: '#1e293b' },
  scripture: { bg: '#fef3c7', text: '#451a03', accent: '#92400e', secondary: '#78350f', border: '#fbbf24', surface: '#fefce8' },
  ocean: { bg: '#0c4a6e', text: '#f0f9ff', accent: '#38bdf8', secondary: '#bae6fd', border: '#075985', surface: '#0e5f8a' },
  minimal: { bg: '#fafafa', text: '#18181b', accent: '#18181b', secondary: '#71717a', border: '#d4d4d8', surface: '#f4f4f5' },
};

function themeCssVars(theme) {
  const fg = hexToHsl(theme.text);
  const bg = hexToHsl(theme.bg);
  const card = hexToHsl(theme.surface);
  const accent = hexToHsl(theme.accent);
  const muted = hexToHsl(theme.secondary);
  const border = hexToHsl(theme.border);
  return {
    '--foreground': fg, '--background': bg, '--card': card, '--card-foreground': fg,
    '--popover': card, '--popover-foreground': fg, '--primary': accent, '--primary-foreground': bg,
    '--secondary': card, '--secondary-foreground': fg, '--muted': card, '--muted-foreground': muted,
    '--accent': accent, '--accent-foreground': bg, '--border': border, '--input': border, '--ring': accent,
    '--theme-text': theme.text, '--theme-textSecondary': theme.secondary, '--theme-accent': theme.accent,
    '--theme-background': theme.bg, '--theme-surface': theme.surface, '--theme-border': theme.border,
    '--theme-hover': theme.surface, '--glass-border': theme.border,
  };
}

const SUGGESTED_TOPICS = ['Scripture', 'Prayer', 'Doctrine', 'History', 'Prophecy', 'Wisdom', 'Worship'];

// ──────────────────── Topic Picker (inline) ────────────────────
function TopicPicker({ value, onChange, theme }) {
  const {
    t
  } = useTranslation();

  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');

  const select = (t) => { onChange(t); setOpen(false); setCustom(''); };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg transition-colors"
        style={{ background: value ? `${theme.accent}18` : `${theme.secondary}15`, color: value ? theme.accent : theme.secondary, border: `1px solid ${value ? theme.accent + '40' : theme.border}` }}
        data-testid="topic-picker-btn"
      >
        <Tag className="h-3 w-3" />
        {value || 'Add topic'}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute bottom-full left-0 mb-1 z-50 rounded-xl p-2 shadow-lg min-w-[200px]"
          style={{ background: theme.surface, border: `1px solid ${theme.border}` }}
          data-testid="topic-picker-dropdown">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {SUGGESTED_TOPICS.map(t => (
              <button key={t} onClick={() => select(t)}
                className="px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors"
                style={{ background: value === t ? theme.accent : `${theme.secondary}15`, color: value === t ? theme.bg : theme.text }}
                data-testid={`topic-option-${t.toLowerCase()}`}>
                {t}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <input value={custom} onChange={e => setCustom(e.target.value)} placeholder={t('generated.sharedmessagepage_custom_topic_5206')}
              className="flex-1 text-xs px-2.5 py-1.5 rounded-lg bg-transparent border focus:outline-none"
              style={{ borderColor: theme.border, color: theme.text }}
              onKeyDown={e => { if (e.key === 'Enter' && custom.trim()) select(custom.trim()); }}
              data-testid="topic-custom-input" />
            {custom.trim() && (
              <button onClick={() => select(custom.trim())} className="p-1 rounded" style={{ color: theme.accent }}>
                <Check className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {value && (
            <button onClick={() => { onChange(''); setOpen(false); }} className="mt-1.5 text-[11px] w-full text-left px-1" style={{ color: theme.secondary }}>
              {t('generated.sharedmessagepage_remove_topic_0ca7')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// ──────────────────── Main Component ────────────────────
export default function SharedMessagePage() {
  const {
    t
  } = useTranslation();

  const { shareId } = useParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Auth state — detect if user is logged in
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saveTopic, setSaveTopic] = useState('');
  const [saving, setSaving] = useState(false);
  const [discussing, setDiscussing] = useState(false);

  // Try to access theme context (only works when wrapped in ThemeProvider — i.e. in-app)
  let themeCtx = null;
  try {
    themeCtx = useTheme(); // eslint-disable-line react-hooks/rules-of-hooks
  } catch { /* not in ThemeProvider — external view */ }

  // Fetch shared message
  useEffect(() => {
    const fetchSharedMessage = async () => {
      try {
        const response = await axios.get(`${API}/share/${shareId}`);
        setMessage(response.data);
      } catch {
        setError('Message not found or expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchSharedMessage();
  }, [shareId]);

  // Check auth status (silent — no redirect)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true, timeout: 3000 });
        if (res.data && !res.data.is_guest) {
          setUser(res.data);
          // Check if already saved
          try {
            const savedRes = await axios.get(`${API}/saved-shares`, { withCredentials: true, timeout: 3000 });
            const found = savedRes.data.saved_shares?.find(s => s.share_id === shareId);
            if (found) { setIsSaved(true); setSaveTopic(found.topic || ''); }
          } catch { /* ignore */ }
        }
      } catch { /* Not logged in — that's fine */ }
      setAuthChecked(true);
    };
    checkAuth();
  }, [shareId]);

  const handleSave = useCallback(async () => {
    if (!user || isSaved) return;
    setSaving(true);
    try {
      await axios.post(`${API}/saved-shares`, { share_id: shareId, topic: saveTopic }, { withCredentials: true });
      setIsSaved(true);
      toast.success(t('generated.sharedmessagepage_saved_to_your_7048'));
    } catch (err) {
      if (err.response?.status === 409) {
        setIsSaved(true);
        toast.info(t('generated.sharedmessagepage_already_saved_42f4'));
      } else {
        toast.error(t('generated.sharedmessagepage_failed_to_save_7935'));
      }
    }
    setSaving(false);
  }, [user, isSaved, shareId, saveTopic]);

  const handleTopicChange = useCallback(async (topic) => {
    setSaveTopic(topic);
    // If already saved, update the topic on the server
    if (isSaved) {
      try {
        const savedRes = await axios.get(`${API}/saved-shares`, { withCredentials: true });
        const found = savedRes.data.saved_shares?.find(s => s.share_id === shareId);
        if (found) {
          await axios.put(`${API}/saved-shares/${found.save_id}/topic`, { topic }, { withCredentials: true });
        }
      } catch { /* ignore */ }
    }
  }, [isSaved, shareId]);

  const handleDiscuss = useCallback(async () => {
    if (!user) { navigate('/login'); return; }
    setDiscussing(true);
    try {
      const res = await axios.post(`${API}/conversations/from-share`, { share_id: shareId }, { withCredentials: true });
      navigate(`/chat?conversation=${res.data.conversation_id}`);
    } catch {
      toast.error(t('generated.sharedmessagepage_failed_to_start_f4b5'));
    }
    setDiscussing(false);
  }, [user, shareId, navigate]);

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center" style={{ background: '#0f172a', minHeight: '100dvh' }}>
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center text-center p-4" style={{ background: '#0f172a', color: '#f1f5f9', minHeight: '100dvh' }}>
        <h1 className="text-2xl font-bold mb-4">{t('generated.sharedmessagepage_not_found_4cad')}</h1>
        <p className="text-slate-400 mb-6">{error}</p>
        <Button onClick={() => navigate(homeTarget(), { replace: true })} className="rounded-full">{t('generated.sharedmessagepage_go_home_c200')}</Button>
      </div>
    );
  }

  const themeKey = message.theme || 'classic';
  const theme = THEMES[themeKey] || THEMES.classic;
  const cssVars = themeCssVars(theme);
  const cleanContent = stripFollowUpQuestions(message.content);
  const description = cleanContent.substring(0, 150) + (cleanContent.length > 150 ? '...' : '');
  const title = message.conversation_title || 'Eili - Biblical Research';
  const ogImageUrl = `${BACKEND_URL}/icon-512.png`;
  const isLoggedIn = authChecked && user;

  return (
    <>
      <Helmet>
        <title>{title} {t('generated.sharedmessagepage_eili_9586')}</title>
        <meta name="description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>
      <div className="page-container" style={{ background: theme.bg, color: theme.text, ...cssVars }}>

        {/* Header */}
        <header
          className="px-4 py-4 shadow-sm sticky top-0 z-50"
          style={{ background: theme.surface, borderBottom: `1px solid ${theme.border}` }}
        >
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(homeTarget(), { replace: true })}>
              <img src="/logo.png" alt={t('generated.sharedmessagepage_eili_9847')} className="h-8 w-8 object-contain" />
              <span className="font-semibold text-lg" style={{ fontFamily: "'Playwrite ES Deco', cursive", color: theme.accent }}>
                {t('generated.sharedmessagepage_eili_9847')}
              </span>
            </div>
            {isLoggedIn ? (
              <Button
                variant="outline"
                onClick={() => navigate(homeTarget(), { replace: true })}
                className="rounded-full"
                style={{ borderColor: theme.border, color: theme.text }}
                data-testid="back-to-app-button"
              >
                {t('generated.sharedmessagepage_back_to_app_16eb')}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => navigate(homeTarget(), { replace: true })}
                className="rounded-full"
                style={{ borderColor: theme.border, color: theme.text }}
                data-testid="open-app-button"
              >
                {t('generated.sharedmessagepage_open_app_651a')}
              </Button>
            )}
          </div>
        </header>

        {/* AI Response */}
        <main className="page-content max-w-3xl mx-auto w-full p-4 md:p-8">
          <div className="h-1 rounded-full mb-6" style={{ background: theme.accent, width: 60 }} />

          <div
            className="rounded-2xl p-5 md:p-8 shadow-sm"
            style={{ background: theme.surface, border: `1px solid ${theme.border}` }}
            data-testid="share-response"
          >
            <div className="flex items-center gap-2 mb-4">
              <img src="/logo.png" alt={t('generated.sharedmessagepage_eili_9847')} className="h-6 w-6 object-contain" />
              <span className="text-xs font-medium uppercase tracking-wide" style={{ color: theme.secondary }}>
                {t('generated.sharedmessagepage_eili_response_53cd')}
              </span>
            </div>
            <div style={{ color: theme.text }}>
              <MessageDisplay content={cleanContent} />
            </div>
          </div>

          {/* ──── In-App Actions (logged in users) ──── */}
          {isLoggedIn && (
            <div className="mt-6 space-y-3" data-testid="share-actions-authenticated">
              {/* Save / Topic row */}
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  onClick={handleSave}
                  disabled={saving || isSaved}
                  className="rounded-full gap-2 px-5"
                  style={isSaved
                    ? { background: `${theme.accent}15`, color: theme.accent, border: `1px solid ${theme.accent}40` }
                    : { background: theme.accent, color: theme.bg }
                  }
                  data-testid="save-share-btn"
                >
                  {isSaved ? <BookmarkCheck className="h-4 w-4" /> : <BookmarkPlus className="h-4 w-4" />}
                  {saving ? 'Saving...' : isSaved ? 'Saved' : 'Save to Library'}
                </Button>
                <TopicPicker value={saveTopic} onChange={handleTopicChange} theme={theme} />
              </div>

              {/* Discuss with Eili */}
              <Button
                onClick={handleDiscuss}
                disabled={discussing}
                variant="outline"
                className="rounded-full gap-2 px-5"
                style={{ borderColor: theme.accent, color: theme.accent }}
                data-testid="discuss-share-btn"
              >
                <MessageSquareText className="h-4 w-4" />
                {discussing ? 'Starting discussion...' : 'Discuss with Eili'}
              </Button>
            </div>
          )}

          {/* ──── CTA for non-logged-in users ──── */}
          {!isLoggedIn && authChecked && (
            <div className="text-center space-y-3 mt-8 mb-8" data-testid="share-cta-unauthenticated">
              <h2 className="text-lg font-semibold" style={{ color: theme.text }}>
                {t('generated.sharedmessagepage_ask_your_own_44ec')}
              </h2>
              <p className="text-sm max-w-md mx-auto" style={{ color: theme.secondary }}>
                {t('generated.sharedmessagepage_get_instant_scripture_6208')}
              </p>
              <div className="flex justify-center gap-3">
                <Button
                  size="lg"
                  onClick={() => navigate('/signup')}
                  className="rounded-full px-8"
                  style={{ background: theme.accent, color: theme.bg }}
                  data-testid="share-get-started"
                >
                  {t('generated.sharedmessagepage_get_started_8541')}
                </Button>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
}
